import { useState, useEffect } from "react"
import { Link } from "@tanstack/react-router"
import freeQuotation from "@/assets/free_quotation.jpg"
import transparentPricing from "@/assets/transparent_pricing.jpg"
import supplierReach from "@/assets/supplier_reach.jpg"
import simpleWorkflow from "@/assets/simple_workflow.jpg"
import globalSupport from "@/assets/global_support.jpg"
import totalVisibility from "@/assets/total_visibility.jpg"

const steps = [
  {
    id: 1,
    label: "Free Quotation",
    brief: "Get fast custom manufacturer pricing.",
    img: freeQuotation,
    className: "md:translate-y-0"
  },
  {
    id: 2,
    label: "Transparent Pricing",
    brief: "No hidden fees, complete visual breakdown.",
    img: transparentPricing,
    className: "md:translate-y-24"
  },
  {
    id: 3,
    label: "Supplier Reach",
    brief: "Find audited factories in India.",
    img: supplierReach,
    className: "md:translate-y-44"
  },
  {
    id: 4,
    label: "Simple Workflow",
    brief: "Track milestones at every step.",
    img: simpleWorkflow,
    className: "md:translate-y-44"
  },
  {
    id: 5,
    label: "Global Support",
    brief: "Helpful assistance at every step.",
    img: globalSupport,
    className: "md:translate-y-24"
  },
  {
    id: 6,
    label: "Total Visibility",
    brief: "Real-time updates on your order.",
    img: totalVisibility,
    className: "md:translate-y-0"
  }
]

export function HowItWorks() {
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null)
  const [activeCardId, setActiveCardId] = useState<number | null>(null)

  const isHighlighted = hoveredCardId !== null || activeCardId !== null

  const handleCardClick = (id: number) => {
    if (activeCardId === id || hoveredCardId === id) {
      setActiveCardId(null)
      setHoveredCardId(null)
    } else {
      setActiveCardId(id)
      setHoveredCardId(id)
    }
  }

  useEffect(() => {
    const handleGlobalPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null
      if (target && !target.closest("[data-step-card]")) {
        setActiveCardId(null)
        setHoveredCardId(null)
      }
    }
    window.addEventListener("pointerdown", handleGlobalPointerDown)
    window.addEventListener("touchstart", handleGlobalPointerDown, { passive: true })
    return () => {
      window.removeEventListener("pointerdown", handleGlobalPointerDown)
      window.removeEventListener("touchstart", handleGlobalPointerDown)
    }
  }, [])

  return (
    <section id="how-it-works" className="relative py-8 md:py-20 overflow-hidden bg-surface border-y border-outline-variant/60 text-on-surface">
      {/* Background decoration */}
      <div 
        className="absolute inset-0 opacity-[0.25] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(var(--outline-variant) 1px, transparent 1px),
            linear-gradient(90deg, var(--outline-variant) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px"
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center pb-2 md:pb-8">
        
        {/* Sourcing Steps Grid Container */}
        <div className="relative mt-4 max-w-6xl mx-auto">
          
          {/* Section Header (Centered overlay on desktop, stacked on mobile) */}
          <div className="md:absolute md:top-4 md:left-1/2 md:-translate-x-1/2 md:w-[60%] z-20 mb-10 md:mb-0 space-y-4">
            <h2 className="font-sans text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight text-on-surface max-w-xl mx-auto leading-tight">
              Ready to simplify your sourcing process?
            </h2>
            <div className="pt-1">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary/20 text-primary bg-white hover:bg-primary/5 hover:border-primary/50 transition-all font-semibold text-xs cursor-pointer shadow-2xs"
              >
                Get your free custom quote <span className="text-primary font-bold">&gt;</span>
              </Link>
            </div>
          </div>
          
          {/* Connector Dashed Line (Desktop Only) */}
          <div className="absolute inset-x-0 md:top-24 bottom-0 hidden md:block pointer-events-none" aria-hidden="true">
            <svg className="w-full h-full" viewBox="0 0 1200 400" preserveAspectRatio="none">
              <path
                d="M 100,150 L 100,280 L 300,280 L 300,246 M 300,246 L 300,385 L 900,385 L 900,246 M 900,246 L 900,280 L 1100,280 L 1100,150"
                fill="none"
                stroke="var(--primary)"
                strokeWidth="4"
                strokeDasharray={isHighlighted ? "none" : "12 12"}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-100 transition-all duration-300"
                style={{
                  animation: isHighlighted ? "none" : "dashMove 1.5s linear infinite"
                }}
              />
            </svg>
            <style>{`
              @keyframes dashMove {
                from {
                  stroke-dashoffset: 32;
                }
                to {
                  stroke-dashoffset: 0;
                }
              }
            `}</style>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-6 md:gap-4 relative z-10 pb-4 md:pb-36 md:pt-24">
            {steps.map((step) => {
              const isActive = activeCardId === step.id || hoveredCardId === step.id
              return (
                <div
                  key={step.id}
                  data-step-card="true"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCardClick(step.id)
                  }}
                  onMouseEnter={() => setHoveredCardId(step.id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                  className={`relative z-10 transition-all duration-300 cursor-pointer select-none ${
                    isActive ? "scale-[1.06] z-20" : ""
                  } ${step.className}`}
                >
                  {/* The visible card container */}
                  <div className={`relative h-full w-full flex flex-col items-center border rounded-2xl p-3 transition-all duration-300 shadow-2xs overflow-hidden ${
                    isActive
                      ? "bg-surface-container-low border-primary/35 md:border-b-transparent shadow-md shadow-primary/5 md:rounded-b-none"
                      : "bg-white border-outline-variant/60"
                  }`}>
                    {/* Large Background Step Number */}
                    <span className="absolute -bottom-4 -left-2 text-[100px] font-black text-primary/5 pointer-events-none select-none z-0">
                      {step.id}
                    </span>

                    {/* White Image Container */}
                    <div className="w-full bg-white rounded-xl overflow-hidden aspect-[4/3] flex items-center justify-center p-0 z-10 border border-outline-variant/40">
                      <img
                        src={step.img}
                        alt={step.label}
                        className="w-full h-full object-contain"
                        draggable={false}
                      />
                    </div>

                    {/* Label */}
                    <h4 className="mt-4 text-sm font-bold text-on-surface tracking-wide z-10 text-center">
                      {step.label}
                    </h4>

                    {/* Mobile Brief (Inline) */}
                    <div
                      className={`md:hidden border-t border-outline-variant/30 p-2 mt-2 transition-all duration-300 z-30 text-center w-full`}
                    >
                      <p className="text-[11px] text-[#5f5e5e] font-semibold leading-normal">
                        {step.brief}
                      </p>
                    </div>
                  </div>

                  {/* Desktop Brief (Absolute Dropdown) */}
                  <div
                    className={`hidden md:block absolute top-[100%] left-0 w-full border-x border-b rounded-b-2xl p-2 shadow-md transition-all duration-300 z-30 text-center -mt-px ${
                      isActive
                        ? "bg-surface-container-low border-primary/35 opacity-100 pointer-events-auto"
                        : "bg-white border-outline-variant/60 opacity-0 pointer-events-none"
                    }`}
                  >
                    <p className="text-[11px] text-[#5f5e5e] font-semibold leading-normal">
                      {step.brief}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

        </div>

      </div>
    </section>
  )
}
